import React, { useState, useEffect } from 'react';
import { Modal, Button, Label, TextInput, Select, Alert } from 'flowbite-react';
import { HiUser } from 'react-icons/hi';
import type { PersonResponse } from '../../types/api';
import { dateInputBounds, validateDateField } from '../../utils/dateValidation';

interface EditPersonInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  person: PersonResponse;
  onSave: (updates: Partial<PersonResponse>) => Promise<void>;
}

export const EditPersonInfoModal: React.FC<EditPersonInfoModalProps> = ({
  isOpen,
  onClose,
  person,
  onSave
}) => {
  const [formData, setFormData] = useState({
    honorific: '',
    firstName: '',
    middleName: '',
    lastSurname: '',
    generationCodeSuffix: '',
    preferredName: '',
    birthDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (person) {
      setFormData({
        honorific: person.honorific || '',
        firstName: person.firstName || '',
        middleName: person.middleName || '',
        lastSurname: person.lastSurname || '',
        generationCodeSuffix: person.generationCodeSuffix || '',
        preferredName: person.preferredName || '',
        birthDate: person.birthDate || ''
      });
    }
  }, [person]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const dateError = validateDateField(formData.birthDate, 'Birth Date');
    if (dateError) {
      setError(dateError);
      return;
    }

    setLoading(true);

    try {
      // Convert empty strings to null for optional fields
      const dataToSend = {
        ...formData,
        honorific: formData.honorific || null,
        middleName: formData.middleName || null,
        generationCodeSuffix: formData.generationCodeSuffix || null,
        preferredName: formData.preferredName || null,
        birthDate: formData.birthDate || null,
      };
      await onSave(dataToSend);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update person information');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="md">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <HiUser className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900">
            Edit Person Information
          </h3>
        </div>

        {error && (
          <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="honorific" className="block mb-2 text-sm font-medium text-gray-700">
              Honorific
            </Label>
            <Select
              id="honorific"
              value={formData.honorific}
              onChange={(e) => setFormData({ ...formData, honorific: e.target.value })}
              disabled={loading}
              className="w-32"
            >
              <option value="">None</option>
              <option value="Dr.">Dr.</option>
              <option value="Mr.">Mr.</option>
              <option value="Mrs.">Mrs.</option>
              <option value="Ms.">Ms.</option>
              <option value="Prof.">Prof.</option>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName" className="block mb-2 text-sm font-medium text-gray-700">
                First Name <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="firstName"
                type="text"
                placeholder="First name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="middleName" className="block mb-2 text-sm font-medium text-gray-700">
                Middle Name
              </Label>
              <TextInput
                id="middleName"
                type="text"
                placeholder="Middle name (optional)"
                value={formData.middleName}
                onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="lastSurname" className="block mb-2 text-sm font-medium text-gray-700">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="lastSurname"
                type="text"
                placeholder="Last name"
                value={formData.lastSurname}
                onChange={(e) => setFormData({ ...formData, lastSurname: e.target.value })}
                required
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="generationCodeSuffix" className="block mb-2 text-sm font-medium text-gray-700">
                Suffix
              </Label>
              <Select
                id="generationCodeSuffix"
                value={formData.generationCodeSuffix}
                onChange={(e) => setFormData({ ...formData, generationCodeSuffix: e.target.value })}
                disabled={loading}
              >
                <option value="">None</option>
                <option value="Jr.">Jr.</option>
                <option value="Sr.">Sr.</option>
                <option value="II">II</option>
                <option value="III">III</option>
                <option value="IV">IV</option>
                <option value="V">V</option>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="preferredName" className="block mb-2 text-sm font-medium text-gray-700">
              Preferred Name
            </Label>
            <TextInput
              id="preferredName"
              type="text"
              placeholder="Name used for display and communication"
              value={formData.preferredName}
              onChange={(e) => setFormData({ ...formData, preferredName: e.target.value })}
              disabled={loading}
            />
          </div>

          <div>
            <Label htmlFor="birthDate" className="block mb-2 text-sm font-medium text-gray-700">
              Birth Date
            </Label>
            <TextInput
              id="birthDate"
              type="date"
              value={formData.birthDate}
              onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
              disabled={loading}
              {...dateInputBounds}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button color="gray" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};